/** A room's sign-up page and offer, read and changed in place, for the computer's tab and Season's. */
import { useEffect, useMemo, useState } from "react";
import { liveFill, seriesPageTime } from "../../../shared/live-page";
import type { OfferJson, RegField, WebinarDetail } from "../../../shared/types";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";

/** The page as a visitor sees it now. Scripts stay off wherever it is drawn, so drawing it counts no visit. */
export function usePageDoc(detail: WebinarDetail): string | null {
  const [html, setHtml] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api.page
      .get(detail.id)
      .then((res) => alive && setHtml(res.html))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [detail.id]);
  const live = detail.live;
  return useMemo(() => {
    if (!html) return null;
    if (!live) return html;
    return liveFill(html, live.series ? seriesPageTime(live.series) : { startsAt: live.startsAt, timezone: live.timezone, state: live.state }, Date.now());
  }, [html, live]);
}

/** Where a link goes, as people read it: "mayatorres.com/book". */
export function linkWords(url: string): string {
  return url.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
}

export function usePageTab(detail: WebinarDetail, onChanged: () => void) {
  const toast = useToast();
  const [confirm, setConfirm] = useState(detail.confirmation_on);
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState<RegField[]>(detail.reg_fields);
  const [asking, setAsking] = useState(false);
  const [offer, setOffer] = useState(detail.offer);
  const [editing, setEditing] = useState(false);
  useEffect(() => setConfirm(detail.confirmation_on), [detail.confirmation_on]);
  useEffect(() => setFields(detail.reg_fields), [detail.reg_fields]);
  useEffect(() => setOffer(detail.offer), [detail.offer]);

  const visits = detail.reg_stats?.visits ?? 0;
  const registered = detail.registered_here;
  const rate = visits ? Math.round((registered / visits) * 100) : 0;
  const phone = fields.some((f) => f.key === "phone");
  const fixed = fields.length
    ? fields.filter((f) => ["first_name", "email"].includes(f.key))
    : [
        { key: "first_name", label: "First name" },
        { key: "email", label: "Email" },
      ];
  const extra = fields.filter((f) => !["first_name", "last_name", "email", "phone"].includes(f.key));

  // The phone is asked for on the page itself: its form is what the sign-up collects (ai/page/fields.ts).
  const flipPhone = (on: boolean) => {
    if (asking) return;
    setAsking(true);
    setFields((was) => (on ? [...was, { key: "phone", label: "Phone", type: "tel", required: false }] : was.filter((f) => f.key !== "phone")));
    api.page
      .phone(detail.id, on)
      .then((res) => {
        setFields(res.reg_fields);
        toast(`Asking for a phone number is ${on ? "on" : "off"}.`);
        onChanged();
      })
      .catch((err: unknown) => {
        setFields(detail.reg_fields);
        toast(err instanceof ApiError ? err.message : "That didn't save. Try again.");
      })
      .finally(() => setAsking(false));
  };

  const flipConfirm = (on: boolean) => {
    if (busy) return;
    setConfirm(on);
    setBusy(true);
    api.webinars
      .setConfirmation(detail.id, on)
      .then((res) => {
        setConfirm(res.confirmation_on);
        toast(`The confirmation email is ${res.confirmation_on ? "on" : "off"}.`);
      })
      .catch((err: unknown) => {
        setConfirm(!on);
        toast(err instanceof ApiError ? err.message : "That didn't save. Try again.");
      })
      .finally(() => setBusy(false));
  };

  return {
    visits,
    registered,
    rate,
    phone,
    fixed,
    extra,
    confirm,
    busy,
    asking,
    flipPhone,
    flipConfirm,
    offer: offer as OfferJson | null,
    setOffer,
    editing,
    setEditing,
  };
}
